/**
 * Personal risk settings and lockout requests as the UI edits them (SPEC §10.2, SPEC-ADDENDA A3,
 * owner decisions of 2026-09-27 in commit e2cd271). Wire shapes mirror
 * `crates/domain/src/risk/{personal,lockout,mod}.rs` serde exactly; the client-side checks mirror
 * `PersonalLimits::validate` so the user sees an error before saving, and the gateway
 * re-validates. Nothing here enforces or sends anything.
 */
import { zonedWallTimeToUtc } from "@helm/core/time";

// ---------------------------------------------------------------------------------------------
// Limit actions (domain `LimitAction`, externally tagged, snake_case)

/** Domain `HardStopLockout` (tag = "type"). */
export type HardStopLockoutJson = { type: "rest_of_session" } | { type: "minutes"; minutes: number };

/**
 * Domain `LimitAction` as the UI writes it: `"liquidate"`, `{"liquidate_and_lockout":{…}}` or the
 * observe mode `{"lockout":{…}}` (lock out, keep positions). The domain's older
 * `"liquidate_and_block"` is no longer offered (owner decision 2026-09-27); stored values migrate to
 * the hard stop in {@link sanitizeLimits}.
 */
export type LimitActionJson =
  | "liquidate"
  | { liquidate_and_lockout: { lockout: HardStopLockoutJson } }
  | { lockout: { lockout: HardStopLockoutJson } };

export type ActionChoice = "liquidate" | "liquidate_and_lockout" | "lockout";
export type HardStopLockoutChoice = "rest_of_session" | "minutes";

/** One entry of a stop's "when hit" select (owner decisions 2026-09-27, Q-R4 / Q-R7). */
export type ActionOption = "flatten_lock_session" | "flatten_lock_minutes" | "lock_only" | "flatten_only";

/** The owner's four choices, default first. */
export const ACTION_OPTIONS: readonly { key: ActionOption; label: string }[] = [
  { key: "flatten_lock_session", label: "Flatten + lock out until session end" },
  { key: "flatten_lock_minutes", label: "Flatten + lock out for N minutes" },
  { key: "lock_only", label: "Lock out only (observe — keep positions)" },
  { key: "flatten_only", label: "Flatten only (keep trading)" },
];

/** Domain `LimitAction::HARD_STOP`: flatten and lock out until the session close. */
export const HARD_STOP: LimitActionJson = { liquidate_and_lockout: { lockout: { type: "rest_of_session" } } };

/** `PersonalLimits` JSON (serde, `deny_unknown_fields`): these keys and no others. Integer cents. */
export type PersonalLimitsJson = {
  pdll_cents: number | null;
  pdll_action: LimitActionJson;
  pdpt_soft_cents: number | null;
  pdpt_cents: number | null;
  pdpt_action: LimitActionJson;
  daily_trail_cents: number | null;
  daily_trail_action: LimitActionJson;
  rearm_buffer_cents: number;
  warn: { warn_pct: number; danger_pct: number };
};

/**
 * Domain `PersonalLimits::default()`: every limit empty (owner Q-R8), every stop a hard stop
 * (flatten + lock out until session end, Q-R4), $50 re-arm buffer, warnings at 75 % / 90 %.
 */
export const DEFAULT_LIMITS: PersonalLimitsJson = {
  pdll_cents: null,
  pdll_action: HARD_STOP,
  pdpt_soft_cents: null,
  pdpt_cents: null,
  pdpt_action: HARD_STOP,
  daily_trail_cents: null,
  daily_trail_action: HARD_STOP,
  rearm_buffer_cents: 5000,
  warn: { warn_pct: 75, danger_pct: 90 },
};

/** Largest u32 (domain `minutes: u32`). */
const U32_MAX = 4_294_967_295;

/** Form state for one limit's action. */
export type ActionForm = { choice: ActionChoice; lockout: HardStopLockoutChoice; minutes: string };

function actionToForm(a: LimitActionJson): ActionForm {
  if (a === "liquidate") return { choice: a, lockout: "rest_of_session", minutes: "30" };
  const [choice, l] =
    "lockout" in a
      ? (["lockout", a.lockout.lockout] as const)
      : (["liquidate_and_lockout", a.liquidate_and_lockout.lockout] as const);
  return l.type === "minutes"
    ? { choice, lockout: "minutes", minutes: String(l.minutes) }
    : { choice, lockout: "rest_of_session", minutes: "30" };
}

/** Which select option a form action shows. */
export function actionOptionOf(a: ActionForm): ActionOption {
  switch (a.choice) {
    case "liquidate":
      return "flatten_only";
    case "lockout":
      return "lock_only";
    case "liquidate_and_lockout":
      return a.lockout === "minutes" ? "flatten_lock_minutes" : "flatten_lock_session";
  }
}

/** Apply a select option; the minutes typed so far are kept. */
export function withActionOption(a: ActionForm, o: ActionOption): ActionForm {
  switch (o) {
    case "flatten_lock_session":
      return { ...a, choice: "liquidate_and_lockout", lockout: "rest_of_session" };
    case "flatten_lock_minutes":
      return { ...a, choice: "liquidate_and_lockout", lockout: "minutes" };
    case "lock_only":
      return { ...a, choice: "lockout" };
    case "flatten_only":
      return { ...a, choice: "liquidate" };
  }
}

function lockoutWords(l: HardStopLockoutJson): string {
  return l.type === "minutes"
    ? `for ${String(l.minutes)} minute${l.minutes === 1 ? "" : "s"}`
    : "until end of session";
}

/** Human description of an action, e.g. "flatten, then lock out for 30 minutes". */
export function describeAction(a: LimitActionJson): string {
  if (a === "liquidate") return "flatten; trading continues";
  if ("lockout" in a) return `lock out ${lockoutWords(a.lockout.lockout)}; positions are kept`;
  return `flatten, then lock out ${lockoutWords(a.liquidate_and_lockout.lockout)}`;
}

// ---------------------------------------------------------------------------------------------
// Form

/** Form state: what the user typed ($ strings), before validation. */
export type RiskForm = {
  pdll: string;
  pdllAction: ActionForm;
  pdptSoft: string;
  pdpt: string;
  pdptAction: ActionForm;
  trail: string;
  trailAction: ActionForm;
  rearm: string;
  warnPct: string;
  dangerPct: string;
};

export type RiskField =
  "pdll" | "pdllAction" | "pdptSoft" | "pdpt" | "pdptAction" | "trail" | "trailAction" | "rearm" | "warn";

export type RiskValidation =
  { ok: true; limits: PersonalLimitsJson } | { ok: false; errors: Partial<Record<RiskField, string>> };

/** Largest cents value we accept: keeps every amount an exact JS integer (and a sane $ figure). */
const MAX_CENTS = 100_000_000_00; // $100M

/**
 * `"1,200.50"` → 120050. `""` → null (limit disabled). Returns NaN for anything that is not a
 * plain dollar amount with at most 2 decimals.
 */
export function parseDollarsToCents(input: string): number | null {
  const s = input.trim().replace(/[$,\s]/g, "");
  if (s === "") return null;
  const m = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(s);
  if (!m?.[2]) return Number.NaN;
  const cents = Number(m[2]) * 100 + Number((m[3] ?? "").padEnd(2, "0"));
  if (!Number.isSafeInteger(cents) || cents > MAX_CENTS) return Number.NaN;
  return m[1] ? -cents : cents;
}

/** 120050 → "1200.50", null → "" (for filling the form). */
export function centsToInput(cents: number | null): string {
  if (cents === null) return "";
  const neg = cents < 0;
  const a = Math.abs(cents);
  const s = `${String(Math.trunc(a / 100))}.${String(a % 100).padStart(2, "0")}`;
  return neg ? `-${s}` : s;
}

export function limitsToForm(l: PersonalLimitsJson): RiskForm {
  return {
    pdll: centsToInput(l.pdll_cents),
    pdllAction: actionToForm(l.pdll_action),
    pdptSoft: centsToInput(l.pdpt_soft_cents),
    pdpt: centsToInput(l.pdpt_cents),
    pdptAction: actionToForm(l.pdpt_action),
    trail: centsToInput(l.daily_trail_cents),
    trailAction: actionToForm(l.daily_trail_action),
    rearm: centsToInput(l.rearm_buffer_cents),
    warnPct: String(l.warn.warn_pct),
    dangerPct: String(l.warn.danger_pct),
  };
}

function parsePct(s: string): number {
  const t = s.trim();
  return /^\d{1,3}$/.test(t) ? Number(t) : Number.NaN;
}

/** Form action → JSON, or an error message (domain `ZeroMinuteLockout` and u32 range). */
function formToAction(
  a: ActionForm,
  name: string,
): { ok: true; action: LimitActionJson } | { ok: false; message: string } {
  if (a.choice === "liquidate") return { ok: true, action: a.choice };
  const wrap = (lockout: HardStopLockoutJson): LimitActionJson =>
    a.choice === "lockout" ? { lockout: { lockout } } : { liquidate_and_lockout: { lockout } };
  if (a.lockout === "rest_of_session") return { ok: true, action: wrap({ type: "rest_of_session" }) };
  const t = a.minutes.trim();
  if (!/^\d{1,10}$/.test(t) || Number(t) > U32_MAX)
    return { ok: false, message: `${name} lockout: enter whole minutes` };
  const minutes = Number(t);
  if (minutes < 1) return { ok: false, message: `${name} lockout must be at least 1 minute` };
  return { ok: true, action: wrap({ type: "minutes", minutes }) };
}

/** Mirrors `PersonalLimits::validate` (plus input parsing). All errors are reported at once. */
export function validateRiskForm(f: RiskForm): RiskValidation {
  const errors: Partial<Record<RiskField, string>> = {};
  const amount = (field: "pdll" | "pdptSoft" | "pdpt" | "trail", name: string): number | null => {
    const c = parseDollarsToCents(f[field]);
    if (c === null) return null;
    if (Number.isNaN(c)) errors[field] = `${name}: enter a dollar amount (e.g. 1200 or 1200.50)`;
    else if (c <= 0) errors[field] = `${name} must be more than $0 (leave empty to turn it off)`;
    return c;
  };
  const pdll = amount("pdll", "Daily max loss");
  const soft = amount("pdptSoft", "Take-profit soft stop");
  const hard = amount("pdpt", "Take-profit hard stop");
  const trail = amount("trail", "Daily trailing limit");

  const rearm = parseDollarsToCents(f.rearm);
  if (rearm === null) errors.rearm = "Re-arm buffer: enter an amount ($0 or more)";
  else if (Number.isNaN(rearm)) errors.rearm = "Re-arm buffer: enter a dollar amount";
  else if (rearm < 0) errors.rearm = "Re-arm buffer cannot be negative";

  if (soft !== null && hard !== null && soft > 0 && hard > 0 && soft >= hard && !errors.pdptSoft) {
    errors.pdptSoft = "Soft stop must be below the hard stop";
  }

  const w = parsePct(f.warnPct);
  const d = parsePct(f.dangerPct);
  if (Number.isNaN(w) || Number.isNaN(d)) errors.warn = "Warn and danger are whole percentages";
  else if (w === 0 || w >= d || d > 100) errors.warn = "Needs 0 < warn % < danger % ≤ 100";

  const act = (field: "pdllAction" | "pdptAction" | "trailAction", name: string): LimitActionJson => {
    const r = formToAction(f[field], name);
    if (r.ok) return r.action;
    errors[field] = r.message;
    return "liquidate";
  };
  const pdllAction = act("pdllAction", "Daily max loss");
  const pdptAction = act("pdptAction", "Take-profit hard stop");
  const trailAction = act("trailAction", "Daily trailing limit");

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return {
    ok: true,
    limits: {
      pdll_cents: pdll,
      pdll_action: pdllAction,
      pdpt_soft_cents: soft,
      pdpt_cents: hard,
      pdpt_action: pdptAction,
      daily_trail_cents: trail,
      daily_trail_action: trailAction,
      rearm_buffer_cents: rearm ?? 0,
      warn: { warn_pct: w, danger_pct: d },
    },
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function sanitizeLockout(l: unknown): HardStopLockoutJson | null {
  if (!isRecord(l)) return null;
  if (l["type"] === "rest_of_session") return { type: "rest_of_session" };
  const m = l["minutes"];
  if (l["type"] === "minutes" && typeof m === "number" && Number.isInteger(m) && m >= 1 && m <= U32_MAX) {
    return { type: "minutes", minutes: m };
  }
  return null;
}

function sanitizeAction(v: unknown, fallback: LimitActionJson): LimitActionJson {
  if (v === "liquidate") return v;
  // The retired "flatten + block until the daily reset" becomes the hard stop it was folded into.
  if (v === "liquidate_and_block") return HARD_STOP;
  if (isRecord(v) && isRecord(v["liquidate_and_lockout"])) {
    const lockout = sanitizeLockout(v["liquidate_and_lockout"]["lockout"]);
    if (lockout) return { liquidate_and_lockout: { lockout } };
  }
  if (isRecord(v) && isRecord(v["lockout"])) {
    const lockout = sanitizeLockout(v["lockout"]["lockout"]);
    if (lockout) return { lockout: { lockout } };
  }
  return fallback;
}

const centsOrNull = (v: unknown): number | null =>
  typeof v === "number" && Number.isSafeInteger(v) && v > 0 ? v : null;

/** Coerce a stored `risk_settings` body into valid limits; unknown keys are dropped. */
export function sanitizeLimits(raw: unknown): PersonalLimitsJson {
  const r = isRecord(raw) ? raw : {};
  const warn = isRecord(r["warn"]) ? r["warn"] : {};
  const wp = warn["warn_pct"];
  const dp = warn["danger_pct"];
  const okWarn =
    typeof wp === "number" &&
    typeof dp === "number" &&
    Number.isInteger(wp) &&
    Number.isInteger(dp) &&
    wp > 0 &&
    wp < dp &&
    dp <= 100;
  const rb = r["rearm_buffer_cents"];
  return {
    pdll_cents: centsOrNull(r["pdll_cents"]),
    pdll_action: sanitizeAction(r["pdll_action"], DEFAULT_LIMITS.pdll_action),
    pdpt_soft_cents: centsOrNull(r["pdpt_soft_cents"]),
    pdpt_cents: centsOrNull(r["pdpt_cents"]),
    pdpt_action: sanitizeAction(r["pdpt_action"], DEFAULT_LIMITS.pdpt_action),
    daily_trail_cents: centsOrNull(r["daily_trail_cents"]),
    daily_trail_action: sanitizeAction(r["daily_trail_action"], DEFAULT_LIMITS.daily_trail_action),
    rearm_buffer_cents: typeof rb === "number" && Number.isSafeInteger(rb) && rb >= 0 ? rb : 5000,
    warn: okWarn ? { warn_pct: wp, danger_pct: dp } : { ...DEFAULT_LIMITS.warn },
  };
}

/**
 * Best day P&L at which the trailing limit arms (domain `trail_arm_at`): the soft target, else
 * the trail amount. Shown as "arms at +$X" until the risk feed says it armed.
 */
export function trailArmAtCents(l: PersonalLimitsJson): number | null {
  if (l.daily_trail_cents === null) return null;
  return l.pdpt_soft_cents ?? l.daily_trail_cents;
}

/** Any limit set (an all-empty document protects nothing). */
export function hasAnyLimit(l: PersonalLimitsJson): boolean {
  return (
    l.pdll_cents !== null ||
    l.pdpt_cents !== null ||
    l.pdpt_soft_cents !== null ||
    l.daily_trail_cents !== null
  );
}

// ---------------------------------------------------------------------------------------------
// Per-account risk settings document (owner Q-U2: limits are per account)

/**
 * Body of the `risk_settings/{accountId}` document: the two per-account fields of the domain
 * `EngineConfig`, under the same names, so the gateway can deserialize it and patch the account's
 * engine config. `personal` is exactly `PersonalLimits` (deny_unknown_fields).
 */
export type AccountRiskJson = {
  personal: PersonalLimitsJson;
  /** `EngineConfig.enforce_drawdown`: own (non-prop) account — HELM flattens and locks at the floor. */
  enforce_drawdown: boolean;
  /**
   * The user's optional per-account contract limits (owner 2026-09-28, ADR-0033): absent = no
   * HELM cap (HELM has no fixed cap of its own; the account's RMS limit is Rithmic's). Positive
   * whole contracts; the gateway refuses 0 as invalid.
   */
  max_order_qty?: number;
  max_position?: number;
  /**
   * OB4 / SPEC §9.8: orders above this many contracts always ask for confirmation, even with
   * one-click on. Never a limit. Absent = over 50 % of the max order quantity when one is known,
   * else `DEFAULT_CONFIRM_QTY_ABOVE` (@helm/core).
   */
  confirm_qty_above?: number;
  /**
   * Firm profile (owner 2026-09-29, ADR-0034): absent or `"auto"` = detected from the account
   * (Rithmic account id, system name, RMS contract limit), `"none"` = no profile, else a seeded
   * profile id. Display and warnings only: nothing is enforced from a profile.
   */
  firm_profile?: string;
  /**
   * Per-account overrides (positive cents; absent = from Rithmic RMS or the profile). They win
   * over both.
   */
  start_balance_cents?: number;
  profit_target_cents?: number;
  daily_loss_cents?: number;
  drawdown_cents?: number;
};

/** The per-account prop overrides, in the order the form shows them. */
export const PROP_OVERRIDES = [
  "start_balance_cents",
  "profit_target_cents",
  "daily_loss_cents",
  "drawdown_cents",
] as const;
export type PropOverride = (typeof PROP_OVERRIDES)[number];

/** A fresh account: every limit empty (owner Q-R8), drawdown not enforced. */
export const DEFAULT_ACCOUNT_RISK: AccountRiskJson = { personal: DEFAULT_LIMITS, enforce_drawdown: false };

/** Coerce a stored `risk_settings/{accountId}` body; unknown keys are dropped. */
export function sanitizeAccountRisk(raw: unknown): AccountRiskJson {
  const r = isRecord(raw) ? raw : {};
  const out: AccountRiskJson = {
    personal: sanitizeLimits(r["personal"]),
    enforce_drawdown: r["enforce_drawdown"] === true,
  };
  const maxOrder = contracts(r["max_order_qty"]);
  const maxPos = contracts(r["max_position"]);
  if (maxOrder !== null) out.max_order_qty = maxOrder;
  if (maxPos !== null) out.max_position = maxPos;
  const confirmAbove = contracts(r["confirm_qty_above"]);
  if (confirmAbove !== null) out.confirm_qty_above = confirmAbove;
  const fp = r["firm_profile"];
  if (typeof fp === "string" && /^[a-z0-9][a-z0-9._-]{0,63}$/.test(fp) && fp !== "auto")
    out.firm_profile = fp;
  for (const k of PROP_OVERRIDES) {
    const c = centsOrNull(r[k]);
    if (c !== null) out[k] = c;
  }
  return out;
}

/**
 * A typed prop override ("" = use Rithmic / the firm profile): `{ ok, value }` with `value` in
 * cents, null for none. Positive dollar amounts only.
 */
export function parsePropOverride(
  text: string,
): { ok: true; value: number | null } | { ok: false; message: string } {
  const c = parseDollarsToCents(text);
  if (c === null) return { ok: true, value: null };
  if (Number.isNaN(c)) return { ok: false, message: "Enter a dollar amount (e.g. 3000), or leave empty" };
  if (c <= 0) return { ok: false, message: "Must be more than $0, or leave empty for the firm profile's" };
  return { ok: true, value: c };
}

/** A positive whole number of contracts, or null. */
function contracts(v: unknown): number | null {
  return typeof v === "number" && Number.isSafeInteger(v) && v >= 1 ? v : null;
}

/**
 * A typed contract limit ("" = none): `{ ok, value }` with `value` null for no limit. Whole
 * contracts ≥ 1 only (no decimals, no 0: leave it empty for no HELM limit).
 */
export function parseContractLimit(
  text: string,
): { ok: true; value: number | null } | { ok: false; message: string } {
  const t = text.trim();
  if (t === "") return { ok: true, value: null };
  if (!/^\d{1,6}$/.test(t)) return { ok: false, message: "Whole contracts (e.g. 35), or empty for no limit" };
  const n = Number(t);
  if (n < 1) return { ok: false, message: "At least 1 contract, or empty for no limit" };
  return { ok: true, value: n };
}

// ---------------------------------------------------------------------------------------------
// Personal lockout (domain `LockoutPreset`, serde tag = "type", snake_case)

export type LockoutPreset =
  | { type: "rest_of_session" }
  | { type: "hours"; hours: number }
  | { type: "until_next_session" }
  /** Absolute UTC instant in ns. A bigint: epoch-ns exceeds 2^53, a JS number would round it. */
  | { type: "until"; until_ns: bigint };

/**
 * JSON for a preset. `until_ns` is written as an exact integer literal (never through a JS
 * number), which is what serde's i64 expects.
 */
export function serializeLockoutPreset(p: LockoutPreset): string {
  switch (p.type) {
    case "until":
      return `{"type":"until","until_ns":${p.until_ns.toString()}}`;
    case "hours":
      return JSON.stringify({ type: "hours", hours: p.hours });
    default:
      return JSON.stringify({ type: p.type });
  }
}

export type LockoutUntilCheck =
  { ok: true; untilNs: bigint; untilMs: number; notice: string | null } | { ok: false; message: string };

/**
 * Resolve a `datetime-local` value ("2026-09-27T18:30") picked **in `zone`** (the user's display
 * zone) to an absolute UTC instant, and check it is in the future (domain
 * `LockoutError::EndNotInFuture`). DST follows `zonedWallTimeToUtc`: a skipped time moves later
 * by the gap and the UI says so; a repeated time takes the first occurrence.
 */
export function resolveLockoutUntil(value: string, zone: string, nowMs: number): LockoutUntilCheck {
  const [date = "", time = ""] = value.trim().split("T");
  const r = zonedWallTimeToUtc(date, time, zone);
  if (!r.ok) return r;
  if (r.ms <= nowMs) return { ok: false, message: "Lockout end must be in the future" };
  const notice =
    r.kind === "gap"
      ? "That time is skipped by a clock change in this zone; the lockout ends the same time after the change."
      : r.kind === "overlap"
        ? "That time happens twice (clock change); the lockout ends at the first one."
        : null;
  return { ok: true, untilMs: r.ms, untilNs: BigInt(r.ms) * 1_000_000n, notice };
}

export type LockoutChoice =
  "rest_of_session" | "1h" | "2h" | "4h" | "custom" | "until_next_session" | "until";

/** Build the preset for a choice; `customHours` / `untilValue` are the raw inputs. */
export function lockoutPresetFor(
  choice: LockoutChoice,
  customHours: string,
  untilValue: string,
  zone: string,
  nowMs: number,
): { ok: true; preset: LockoutPreset } | { ok: false; message: string } {
  switch (choice) {
    case "rest_of_session":
      return { ok: true, preset: { type: "rest_of_session" } };
    case "until_next_session":
      return { ok: true, preset: { type: "until_next_session" } };
    case "1h":
    case "2h":
    case "4h":
      return { ok: true, preset: { type: "hours", hours: Number(choice.slice(0, 1)) } };
    case "custom": {
      const t = customHours.trim();
      if (!/^\d{1,3}$/.test(t) || Number(t) < 1 || Number(t) > 168) {
        return { ok: false, message: "Enter whole hours from 1 to 168" };
      }
      return { ok: true, preset: { type: "hours", hours: Number(t) } };
    }
    case "until": {
      const r = resolveLockoutUntil(untilValue, zone, nowMs);
      return r.ok ? { ok: true, preset: { type: "until", until_ns: r.untilNs } } : r;
    }
  }
}

// ---------------------------------------------------------------------------------------------
// Risk actions pushed by the gateway (domain `RiskAction`, serde tag = "type")

export type LimitKindJson = "pdll" | "pdpt" | "pdpt_soft" | "daily_trail" | "firm_daily_loss" | "drawdown";

/** Which limit fired, in words (owner: a breach message must name the limit). */
export const LIMIT_NAMES: Record<LimitKindJson, string> = {
  pdll: "Daily loss limit",
  pdpt: "Take-profit",
  pdpt_soft: "Take-profit soft stop",
  daily_trail: "Trailing limit",
  firm_daily_loss: "Firm daily loss limit",
  drawdown: "Firm drawdown",
};

export type RiskToast = { tone: "info" | "warn" | "danger" | "success"; title: string; body: string };

function nsToMs(v: unknown): number | null {
  if (typeof v === "bigint") return Number(v / 1_000_000n);
  if (typeof v === "string" && /^-?\d+$/.test(v)) return Number(BigInt(v) / 1_000_000n);
  // A JSON number has lost sub-µs precision already; fine for a displayed minute.
  if (typeof v === "number" && Number.isFinite(v)) return Math.floor(v / 1e6);
  return null;
}

/**
 * Toast for a gateway risk action. `formatInstant` renders absolute times in the user's display
 * zone (lockout end). Unknown or non-user-facing actions return null.
 */
export function riskActionToast(
  action: unknown,
  formatInstant: (ms: number) => string = (ms) => new Date(ms).toISOString(),
): RiskToast | null {
  const t = riskActionToastText(action, formatInstant);
  // Every gateway risk action comes from HELM's own engine and calculation, never Rithmic's
  // figures (ADR-0045): say so wherever it is shown.
  return t === null ? null : { ...t, body: `${t.body} (TradePrizm limit · TradePrizm-calculated)` };
}

function riskActionToastText(action: unknown, formatInstant: (ms: number) => string): RiskToast | null {
  if (!isRecord(action) || typeof action["type"] !== "string") return null;
  const limit = action["limit"];
  const name =
    typeof limit === "string" && limit in LIMIT_NAMES ? LIMIT_NAMES[limit as LimitKindJson] : "Limit";
  switch (action["type"]) {
    case "warn70":
      return { tone: "warn", title: `${name}: warning`, body: "Usage crossed the warn threshold." };
    case "danger90":
      return { tone: "danger", title: `${name}: danger`, body: "Usage crossed the danger threshold." };
    case "liquidate":
      return limit === "pdpt_soft"
        ? {
            tone: "success",
            title: "Take-profit soft stop hit",
            body: "Orders cancelled and positions flattened. You can keep trading today.",
          }
        : {
            tone: "danger",
            title: `${name} hit`,
            body: "Orders cancelled and positions flattened. You can keep trading.",
          };
    case "liquidate_and_block":
      return {
        tone: "danger",
        title: `${name} hit — blocked`,
        body: "Orders cancelled and positions flattened; new orders are blocked until the daily reset.",
      };
    case "liquidate_and_lockout": {
      const l = action["lockout"];
      const how =
        isRecord(l) && l["type"] === "minutes" && typeof l["minutes"] === "number"
          ? `for ${String(l["minutes"])} minutes`
          : "until end of session";
      return {
        tone: "danger",
        title: `${name} hit — locked out`,
        body: `Orders cancelled and positions flattened; locked out ${how}.`,
      };
    }
    case "lockout": {
      const l = action["lockout"];
      const how =
        isRecord(l) && l["type"] === "minutes" && typeof l["minutes"] === "number"
          ? `for ${String(l["minutes"])} minutes`
          : "until end of session";
      return {
        tone: "danger",
        title: `${name} hit — locked out`,
        body: `New orders are blocked ${how}. Open positions and orders were left as they are.`,
      };
    }
    case "liquidate_and_lock_account":
      return {
        tone: "danger",
        title: "Drawdown floor hit — account locked",
        body: "Orders cancelled and positions flattened; the account stays locked until you reset it.",
      };
    case "lockout_started": {
      const ms = nsToMs(action["until_ns"]);
      return {
        tone: "danger",
        title: "Locked out",
        body: ms === null ? "New orders are blocked." : `New orders are blocked until ${formatInstant(ms)}.`,
      };
    }
    case "firm_drawdown_breached":
      return {
        tone: "danger",
        title: "Firm drawdown breached",
        body: "Your firm’s drawdown threshold was reached.",
      };
    case "firm_daily_loss_breached":
      return {
        tone: "danger",
        title: "Firm daily loss limit breached",
        body: "Your firm’s daily loss limit was reached.",
      };
    case "firm_daily_loss_soft":
      return {
        tone: "warn",
        title: "Firm daily loss limit hit",
        body: "Your firm flattens the account for the day.",
      };
    case "guard_flatten":
      return {
        tone: "danger",
        title: "Guard flatten",
        body: "Positions flattened: room to the firm threshold ran out.",
      };
    default:
      return null;
  }
}
